import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faPlus, faTrash, faEdit, faFileCsv,
  faCheckCircle, faExclamationTriangle, faSyncAlt,
  faUniversity, faDollarSign, faBuilding, faCreditCard,
  faChevronLeft, faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

// ============================================================
// 1. UTILITAIRE FETCH JSON SÉCURISÉ
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }

  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// 2. CACHE DEXIE (24h) avec fallback périmé
// ============================================================
const db = new Dexie('BanqueCacheDB');
db.version(1).stores({ banques: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedBanques(key, allowExpired = false) {
  try {
    const entry = await db.banques.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch {
    return null;
  }
}

async function setCachedBanques(key, data) {
  await db.banques.put({ key, data, timestamp: Date.now() });
}

async function getStaleBanques(key) {
  return getCachedBanques(key, true);
}

// ============================================================
// 3. DÉDOUBLONNAGE DES REQUÊTES EN COURS
// ============================================================
const pendingFetches = new Map();

// ============================================================
// 4. CONFIGURATION API
// ============================================================
const API_URL = `${API_CONFIG2.apiUrl}banque/api_banque.php`;

// ============================================================
// 5. COMPOSANT PRINCIPAL
// ============================================================
const ManageBanque = ({ userId, fonction }) => {
  // --- États ---
  const [banques, setBanques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 10 });
  const [dataSource, setDataSource] = useState('cache');

  // --- Modales ---
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingBanque, setEditingBanque] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    nomCompte: '',
    numeroCompte: '',
    solde: 0,
    banqueNom: ''
  });

  // --- Toast ---
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const toastTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // --- Fichier pour l'import ---
  const [selectedFile, setSelectedFile] = useState(null);

  // ============================================================
  // 6. GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, variant });
    toastTimeoutRef.current = setTimeout(() => {
      if (isMounted.current) setToast({ show: false });
      toastTimeoutRef.current = null;
    }, 5000);
  }, []);

  // ============================================================
  // 7. RÉCUPÉRATION DES BANQUES (avec dédoublonnage, annulation, cache)
  // ============================================================
  const fetchBanques = useCallback(async (page = 1, search = '', forceRefresh = false, retryCount = 0) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `page_${page}_search_${search}`;
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setBanques(result.data);
          setPagination(result.pagination);
          setDataSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setDataSource('');

    const fetchPromise = (async () => {
      try {
        if (!forceRefresh) {
          const cached = await getCachedBanques(cacheKey);
          if (cached) {
            if (isMounted.current) {
              setBanques(cached.data);
              setPagination(cached.pagination);
              setDataSource('cache');
              setLoading(false);
              setRefreshing(false);
            }
            return cached;
          }
        }

        const uniqueParam = new Date().getTime();
        const url = `${API_URL}?action=fetch&page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}&unique=${uniqueParam}`;
        const response = await fetch(url, { signal: controller.signal });

        if (response.status === 429) {
          const stale = await getStaleBanques(cacheKey);
          if (stale) {
            if (isMounted.current) {
              setBanques(stale.data);
              setPagination(stale.pagination);
              setDataSource('cache (périmé)');
              showToast('429 – Utilisation du cache (données peut-être obsolètes)', 'warning');
              setLoading(false);
              setRefreshing(false);
            }
            return stale;
          }
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          showToast(`Trop de requêtes. Nouvel essai dans ${waitTime/1000}s`, 'warning');
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchBanques(page, search, forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStaleBanques(cacheKey);
          if (stale) {
            if (isMounted.current) {
              setBanques(stale.data);
              setPagination(stale.pagination);
              setDataSource('cache (périmé)');
              showToast('Erreur réseau – Utilisation du cache', 'warning');
              setLoading(false);
              setRefreshing(false);
            }
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          const payload = { data: data.data, pagination: data.pagination };
          if (isMounted.current) {
            setBanques(data.data);
            setPagination(data.pagination);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            await setCachedBanques(cacheKey, payload);
            if (forceRefresh) {
              showToast('Données actualisées', 'success');
            }
            setLoading(false);
            setRefreshing(false);
          }
          return payload;
        } else {
          throw new Error(data.message || 'Erreur serveur');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        const stale = await getStaleBanques(cacheKey);
        if (stale && isMounted.current) {
          setBanques(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          showToast('Erreur – Utilisation du cache', 'warning');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          showToast(err.message || 'Erreur de chargement', 'danger');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [pagination.limit, showToast]);

  // ============================================================
  // 8. EFFETS
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchBanques(1, searchTerm);
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      pendingFetches.clear();
    };
  }, []);

  // ============================================================
  // 9. RECHERCHE (debounce)
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchBanques(1, val, false);
    }, 400);
  };

  const handleRefresh = () => {
    if (!refreshing && !loading) {
      fetchBanques(pagination.currentPage, searchTerm, true);
    }
  };

  // ============================================================
  // 10. EXPORT CSV
  // ============================================================
  const exportCSV = () => {
    if (!banques.length) {
      showToast('Aucune banque à exporter.', 'warning');
      return;
    }
    const headers = ['ID', 'Nom du compte', 'Numéro de compte', 'Solde', 'Banque'];
    const rows = banques.map(b => [
      b.id,
      b.nomCompte,
      b.numeroCompte,
      parseFloat(b.solde).toFixed(2),
      b.banqueNom
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `banques_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ============================================================
  // 11. CRUD
  // ============================================================
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const action = editingBanque ? 'update' : 'create';
    const method = editingBanque ? 'PUT' : 'POST';

    try {
      const data = await fetchJSON(`${API_URL}?action=${action}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowModal(false);
        const cacheKey = `page_${pagination.currentPage}_search_${searchTerm}`;
        await db.banques.delete(cacheKey);
        fetchBanques(pagination.currentPage, searchTerm, true);
      } else {
        showToast(data.message || 'Erreur', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Erreur serveur', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const data = await fetchJSON(`${API_URL}?action=delete&id=${formData.id}`, { method: 'DELETE' });
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowDeleteModal(false);
        const cacheKey = `page_${pagination.currentPage}_search_${searchTerm}`;
        await db.banques.delete(cacheKey);
        fetchBanques(pagination.currentPage, searchTerm, true);
      } else {
        showToast(data.message || 'Erreur', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Erreur serveur', 'danger');
    } finally {
      setDeleting(false);
    }
  };

  const handleImportCSV = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      showToast('Veuillez sélectionner un fichier CSV', 'danger');
      return;
    }
    setSubmitting(true);
    const formDataFile = new FormData();
    formDataFile.append('file', selectedFile);
    try {
      const data = await fetchJSON(`${API_URL}?action=import`, {
        method: 'POST',
        body: formDataFile
      });
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowImportModal(false);
        setSelectedFile(null);
        const cacheKey = `page_1_search_${searchTerm}`;
        await db.banques.delete(cacheKey);
        fetchBanques(1, searchTerm, true);
      } else {
        showToast(data.message || 'Erreur', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Erreur lors de l\'import', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (item) => {
    setEditingBanque(item);
    setFormData({
      id: item.id,
      nomCompte: item.nomCompte,
      numeroCompte: item.numeroCompte,
      solde: parseFloat(item.solde),
      banqueNom: item.banqueNom
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingBanque(null);
    setFormData({
      id: '',
      nomCompte: '',
      numeroCompte: '',
      solde: 0,
      banqueNom: ''
    });
    setShowModal(true);
  };

  // ============================================================
  // 12. AFFICHAGE DU CHARGEMENT
  // ============================================================
  if (loading && !banques.length && !refreshing) {
    return (
      <div className="text-center py-5" style={{ background: 'radial-gradient(circle at 20% 30%, #132044, #0a0f1f)', minHeight: '60vh' }}>
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement des comptes bancaires...</p>
      </div>
    );
  }

  // ============================================================
  // 13. RENDU PRINCIPAL (version CARDS)
  // ============================================================
  return (
    <>
      <style>{`
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .monetra-page {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: 30px;
          color: var(--text-primary);
          font-family: 'Inter', system-ui, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 1.8rem;
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .badge-source {
          font-size: 11px;
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 10px 24px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: 0.9rem;
          white-space: nowrap;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.4;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          border-color: #ef4444;
          color: #f87171;
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.12);
        }
        .monetra-btn.outline {
          border-color: rgba(255,255,255,0.08);
          color: var(--text-secondary);
        }
        .monetra-btn.outline:hover {
          background: rgba(255,255,255,0.05);
        }

        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 20px;
          transition: all 0.3s ease;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        /* CARTES */
        .card-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1.5rem;
          margin-top: 1.5rem;
        }
        .card-item {
          background: rgba(255,255,255,0.03);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 20px;
          padding: 1.5rem;
          transition: all 0.25s ease;
          display: flex;
          flex-direction: column;
        }
        .card-item:hover {
          background: rgba(255,255,255,0.06);
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.2);
          border-color: rgba(59,130,246,0.2);
        }
        .card-item .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.8rem;
        }
        .card-item .card-header .icon-bank {
          font-size: 1.8rem;
          color: var(--accent-blue);
          opacity: 0.7;
        }
        .card-item .card-body {
          flex: 1;
        }
        .card-item .card-body .label {
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-muted);
        }
        .card-item .card-body .value {
          font-size: 1rem;
          font-weight: 500;
          color: var(--text-primary);
          margin-bottom: 0.4rem;
        }
        .card-item .card-body .balance {
          font-size: 1.4rem;
          font-weight: 700;
          color: #4ade80;
        }
        .card-item .card-actions {
          margin-top: 1rem;
          display: flex;
          gap: 0.8rem;
          border-top: 1px solid rgba(255,255,255,0.04);
          padding-top: 1rem;
        }
        .card-item .card-actions button {
          flex: 1;
          padding: 0.5rem;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.02);
          color: var(--text-secondary);
          transition: 0.2s;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
        }
        .card-item .card-actions button:hover {
          background: rgba(255,255,255,0.06);
        }
        .card-item .card-actions .btn-edit {
          color: var(--accent-blue);
        }
        .card-item .card-actions .btn-edit:hover {
          background: rgba(59,130,246,0.1);
        }
        .card-item .card-actions .btn-delete {
          color: #f87171;
        }
        .card-item .card-actions .btn-delete:hover {
          background: rgba(239,68,68,0.1);
        }

        .pagination-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid rgba(255,255,255,0.04);
        }

        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }

        .monetra-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          color: var(--text-primary);
          padding: 12px;
          width: 100%;
          transition: 0.3s;
        }
        .monetra-input:focus {
          border-color: var(--accent-blue);
          outline: none;
          background: rgba(255,255,255,0.06);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.06);
        }
        .monetra-input::placeholder {
          color: var(--text-muted);
        }
        .monetra-input option {
          background: #0b1428;
          color: var(--text-primary);
        }

        .toast-monetra {
          background: rgba(10, 20, 45, 0.8) !important;
          backdrop-filter: blur(24px) !important;
          -webkit-backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          border-radius: 16px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important;
        }

        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-success { color: var(--accent-blue); }
        .text-danger { color: #ef4444; }
        .text-info { color: var(--accent-blue); }
        .fw-bold { font-weight: 600; }
        .small { font-size: 0.85rem; }
        .m-0 { margin: 0; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-1 { margin-right: 0.25rem; }
        .me-2 { margin-right: 0.5rem; }
        .p-2 { padding: 0.5rem; }
        .p-3 { padding: 1rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .text-end { text-align: right; }
        .text-center { text-align: center; }
        .overflow-hidden { overflow: hidden; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }
        .ms-auto { margin-left: auto; }

        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-secondary);
          opacity: 0.5;
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover {
          opacity: 1;
        }

        @media (max-width: 768px) {
          .monetra-page { padding: 16px; }
          .glass-card { padding: 1.2rem; }
          .card-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="monetra-page">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="display-6 fw-bold mb-1">Gestion des comptes bancaires</h1>
            <p className="text-secondary m-0">Gérez vos comptes bancaires et leurs soldes.</p>
          </div>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <span className={`badge-source ${dataSource === 'live' || dataSource === 'live (forcé)' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button className="monetra-btn outline" onClick={handleRefresh} disabled={refreshing || loading}>
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} />
              {refreshing ? 'Actualisation...' : 'Actualiser'}
            </button>
            <button className="monetra-btn" onClick={exportCSV} disabled={banques.length === 0}>
              <FontAwesomeIcon icon={faFileCsv} /> Exporter CSV
            </button>
            <button className="monetra-btn" onClick={() => setShowImportModal(true)}>
              <FontAwesomeIcon icon={faFileCsv} /> Importer CSV
            </button>
            <button className="monetra-btn primary" onClick={openCreateModal}>
              <FontAwesomeIcon icon={faPlus} /> Ajouter
            </button>
          </div>
        </div>

        {/* Recherche */}
        <div className="glass-card mb-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div className="search-wrapper" style={{ minWidth: '260px', flex: 1, maxWidth: '400px' }}>
              <FontAwesomeIcon icon={faSearch} className="text-secondary" />
              <input
                type="text"
                placeholder="Rechercher (nom du compte, banque, numéro...)"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
            <div className="text-secondary small">
              <span>{pagination.totalRecords} enregistrements</span>
            </div>
          </div>
        </div>

        {/* Grille de cartes */}
        {loading && !refreshing && banques.length === 0 ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : banques.length === 0 ? (
          <div className="text-center py-5 text-secondary">
            <FontAwesomeIcon icon={faUniversity} size="3x" className="mb-3 opacity-25" />
            <p>Aucun compte bancaire trouvé.</p>
          </div>
        ) : (
          <div className="card-grid">
            {banques.map(item => {
              // Convertir l'ID en chaîne pour éviter l'erreur substring
              const idStr = String(item.id);
              return (
                <div className="card-item" key={item.id}>
                  <div className="card-header">
                    <span className="icon-bank">
                      <FontAwesomeIcon icon={faBuilding} />
                    </span>
                    <span className="badge-source" style={{ fontSize: '0.7rem' }}>
                      #{idStr.substring(0, 8)}
                    </span>
                  </div>
                  <div className="card-body">
                    <div className="label">Compte</div>
                    <div className="value">{item.nomCompte}</div>
                    <div className="label mt-2">Banque</div>
                    <div className="value">{item.banqueNom}</div>
                    <div className="label mt-2">Numéro</div>
                    <div className="value"><FontAwesomeIcon icon={faCreditCard} className="me-1" />{item.numeroCompte}</div>
                    <div className="label mt-3">Solde</div>
                    <div className="balance"><FontAwesomeIcon icon={faDollarSign} /> {parseFloat(item.solde).toFixed(2)}</div>
                  </div>
                  <div className="card-actions">
                    <button className="btn-edit" onClick={() => openEditModal(item)} disabled={loading || refreshing}>
                      <FontAwesomeIcon icon={faEdit} /> Modifier 
                    </button>
                    {fonction === 'SUPER ADMIN' &&(
                        <button className="btn-delete" onClick={() => { setFormData(item); setShowDeleteModal(true); }} disabled={loading || refreshing}>
                      <FontAwesomeIcon icon={faTrash} /> Supprimer
                    </button>
                    )}
                   
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        <div className="pagination-bar">
          <span className="text-secondary small">Page {pagination.currentPage} sur {pagination.totalPages}</span>
          <div className="d-flex gap-2">
            <button
              className="monetra-btn p-2"
              disabled={pagination.currentPage <= 1 || loading || refreshing}
              onClick={() => fetchBanques(pagination.currentPage - 1, searchTerm)}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              className="monetra-btn p-2"
              disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing}
              onClick={() => fetchBanques(pagination.currentPage + 1, searchTerm)}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
      </div>

      {/* --- MODALES --- */}

      {/* Modale Ajout/Modification */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="monetra-modal">
        <Form onSubmit={handleFormSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faEdit} className="text-success me-2" />
              {editingBanque ? 'Modifier' : 'Ajouter'} un compte bancaire
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="text-secondary small">Nom du compte</label>
                <input className="monetra-input" name="nomCompte" value={formData.nomCompte} onChange={(e) => setFormData({...formData, nomCompte: e.target.value})} required disabled={submitting} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Banque</label>
                <input className="monetra-input" name="banqueNom" value={formData.banqueNom} onChange={(e) => setFormData({...formData, banqueNom: e.target.value})} required disabled={submitting} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Numéro de compte</label>
                <input className="monetra-input" name="numeroCompte" value={formData.numeroCompte} onChange={(e) => setFormData({...formData, numeroCompte: e.target.value})} required disabled={submitting} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Solde {formData.solde} ($)</label>
                <input type="hidden" step="0.01" className="monetra-input" name="solde" value={formData.solde} onChange={(e) => setFormData({...formData, solde: parseFloat(e.target.value)})} required disabled={submitting} />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)} className="monetra-btn outline" disabled={submitting}>Annuler</Button>
            <Button variant="success" type="submit" className="monetra-btn primary" disabled={submitting}>
              {submitting ? (
                <><FontAwesomeIcon icon={faSyncAlt} spin /> Enregistrement...</>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modale Suppression */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="monetra-modal">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger"><FontAwesomeIcon icon={faExclamationTriangle} /> Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer le compte bancaire <strong>"{formData.nomCompte}"</strong> ?
          <br /><span className="text-secondary small">Cette action est irréversible.</span>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="monetra-btn outline" disabled={deleting}>Annuler</Button>
          <Button variant="danger" onClick={handleDelete} className="monetra-btn danger" disabled={deleting}>
            {deleting ? (
              <><FontAwesomeIcon icon={faSyncAlt} spin /> Suppression...</>
            ) : (
              'Confirmer la suppression'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modale Importation CSV */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} centered className="monetra-modal">
        <Form onSubmit={handleImportCSV}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faFileCsv} className="text-info me-2" />
              Importer des données CSV
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-secondary small">Le fichier doit avoir les colonnes dans cet ordre :<br /> 
            <code>nomCompte, numeroCompte, solde, banqueNom</code></p>
            <input type="file" accept=".csv" className="monetra-input" onChange={(e) => setSelectedFile(e.target.files[0])} required disabled={submitting} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowImportModal(false)} className="monetra-btn outline" disabled={submitting}>Annuler</Button>
            <Button variant="success" type="submit" className="monetra-btn primary" disabled={submitting}>
              {submitting ? (
                <><FontAwesomeIcon icon={faSyncAlt} spin /> Importation...</>
              ) : (
                'Importer'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Toast */}
      <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
        <div className={`toast-monetra p-3 ${toast.show ? 'd-block' : 'd-none'}`} style={{ minWidth: '300px' }}>
          <div className="d-flex align-items-center gap-3">
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button
              className="btn-close-custom ms-auto"
              onClick={() => setToast({ ...toast, show: false })}
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageBanque;